import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, message, Tag, Card, Typography, Empty, Space, Select, Col, Input, Form, DatePicker, Popover } from 'antd';
import { ReloadOutlined, ExportOutlined, EyeOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import FilterPanel from '../../components/common/FilterPanel';
import { storeItemSaleService } from '../../services/storeItemSaleService';
import { itemService } from '../../services/itemService';
import { warehouseService } from '../../services/warehouseService';
import categoryService from '../../services/categoryService';
import dayjs from 'dayjs';
import {
  StoreItemSale,
  StoreItemSaleFilters,
  StoreItemSalePaginationParams,
  StoreItemSaleSortField,
  SortOrder,
  Sale
} from '../../types/store-item-sale';
import { Item, Store } from '../../types/item-store-relationship';
import { StringOption } from '../../types';
import '../../styles/storeItemSale.css';

const { Title } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const StoreItemSaleList: React.FC = () => {
  const { t } = useTranslation();
  
  // State management
  const [loading, setLoading] = useState<boolean>(false);
  const [storeOptionsLoading, setStoreOptionsLoading] = useState<boolean>(false);
  const [itemOptionsLoading, setItemOptionsLoading] = useState<boolean>(false);
  const [divisionOptionsLoading, setDivisionOptionsLoading] = useState<boolean>(false);
  const [storeItemSales, setStoreItemSales] = useState<StoreItemSale[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [current, setCurrent] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);
  const [filters, setFilters] = useState<StoreItemSaleFilters>({});
  const [sortField, setSortField] = useState<StoreItemSaleSortField>();
  const [sortOrder, setSortOrder] = useState<'ascend' | 'descend'>('descend');
  
  // Options for dropdowns
  const [storeOptions, setStoreOptions] = useState<Store[]>([]);
  const [itemOptions, setItemOptions] = useState<Item[]>([]);
  const [divisionOptions, setDivisionOptions] = useState<StringOption[]>([]);

  // Form instance - 注意：现在由FilterPanel内部管理

  // Fetch store item sales data
  const fetchStoreItemSales = async () => {
    setLoading(true);
    try {
      const response = await storeItemSaleService.getStoreItemSales(
        filters,
        {
          currentPage: current,
          pageSize,
          sortBy: sortField,
          sortOrder: sortOrder === 'ascend' ? 'asc' : 'desc'
        }
      );

      if (response.success && response.data) {
        setStoreItemSales(response.data.tableData);
        setTotal(response.data.paging.totalCount);
      } else {
        console.error(t('storeItemSale.list.messages.dataFormatError'), response);
        setStoreItemSales([]);
        setTotal(0);
        if (response.errorMessage) {
          message.error(response.errorMessage);
        }
      }
    } catch (error) {
      message.error(t('storeItemSale.list.messages.fetchFailed'));
      console.error(t('storeItemSale.list.messages.fetchFailed'), error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch store options
  const fetchStoreOptions = async (searchValue: string = '') => {
    setStoreOptionsLoading(true);
    try {
      const response = await warehouseService.listWarehouse({searchStr:searchValue});
      if (response.success && response.data) {
        setStoreOptions(response.data);
      }
    } catch (error) {
      console.error(t('storeItemSale.list.messages.fetchStoreOptionsFailed'), error);
    } finally {
      setStoreOptionsLoading(false);
    }
  };

  // Fetch item options
  const fetchItemOptions = async (searchValue: string = '') => {
    setItemOptionsLoading(true);
    try {
      let req = {
        searchStr: searchValue,
        pageSize: 100,
        currentPage: 1
      };
      const response = await itemService.listItems(req);
      if (response.success && response.data) {
        setItemOptions(response.data);
      }
    } catch (error) {
      console.error(t('storeItemSale.list.messages.fetchItemOptionsFailed'), error);
    } finally {
      setItemOptionsLoading(false);
    }
  };

  // Fetch division options
  const fetchDivisionOptions = async (searchValue: string = '') => {
    setDivisionOptionsLoading(true);
    try {
      const response = await categoryService.listDivision({ searchStr: searchValue });
      if (response.success && response.data) {
        setDivisionOptions(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch division options', error);
    } finally {
      setDivisionOptionsLoading(false);
    }
  };

  useEffect(() => {
    fetchStoreItemSales();
  }, [current, pageSize, filters, sortField, sortOrder]);

  useEffect(() => {
    fetchStoreOptions();
    fetchItemOptions();
    fetchDivisionOptions();
  }, []);

  const handleTableChange = (
    pagination: any,
    _filters: any,
    sorter: any
  ) => {
    setCurrent(pagination.current);
    setPageSize(pagination.pageSize);

    if (sorter.field && sorter.order) {
      setSortField(sorter.field);
      setSortOrder(sorter.order);
    }
  };

  const handleSearch = (values: any) => {
    const newFilters: StoreItemSaleFilters = {};
    
    if (values.storeCode) {
      newFilters.storeCode = values.storeCode;
    }
    
    if (values.itemCode) {
      newFilters.itemCode = values.itemCode;
    }
    
    if (values.divisionCode) {
      newFilters.divisionCode = values.divisionCode;
    }
    
    if (values.dateRange && values.dateRange.length === 2) {
      newFilters.gmtCreateStart = values.dateRange[0].startOf('day').valueOf();
      newFilters.gmtCreateEnd = values.dateRange[1].endOf('day').valueOf();
    }
    
    setFilters(newFilters);
    setCurrent(1);
  };

  const handleReset = () => {
    setFilters({});
    setCurrent(1);
  };

  const handleRefresh = () => {
    fetchStoreItemSales();
  };

  // Format datetime display
  const formatDateTime = (timestamp: number): string => {
    return dayjs(timestamp).format('YYYY-MM-DD HH:mm:ss');
  };

  const formatSalesQuantity = (quantity: number): string => {
    return quantity.toFixed(2);
  };

  const renderSalesPreview = (sales: Sale[] | number[]) => {
    if (!sales || sales.length === 0) {
      return <span className="text-gray-400">{t('storeItemSale.list.sales.noData')}</span>;
    }

    // 处理数字数组和对象数组两种格式
    let totalQuantity = 0;
    if (Array.isArray(sales) && sales.length > 0) {
      if (typeof sales[0] === 'number') {
        // 数字数组
        totalQuantity = (sales as number[]).reduce((sum, qty) => sum + qty, 0);
      } else {
        // Sale 对象数组
        totalQuantity = (sales as Sale[]).reduce((sum, sale) => sum + (sale.forecastQty || 0), 0);
      }
    }
  
    const content = (
      <div className="sales-preview-popover">
        <div className="sales-summary">
          <strong>{t('storeItemSale.list.sales.totalSales')}: {formatSalesQuantity(totalQuantity)}</strong>
        </div>
        <div className="sales-details">
          {Array.isArray(sales) && sales.map((sale, index) => (
            <div key={index} className="sale-item">
              <span className="sale-date">
                {typeof sale === 'number' ? '' : sale.forcastDate}
              </span>
              <span className="sale-quantity">
                {typeof sale === 'number' ? formatSalesQuantity(sale) : formatSalesQuantity(sale.forecastQty || 0)}
              </span>
            </div>
          ))}
        </div>
      </div>
    );

    return (
      <Popover content={content} title={t('storeItemSale.list.sales.detailTitle')} trigger="hover">
        <div className="sales-preview">
          <Tag color="blue">{t('storeItemSale.list.sales.total')}: {formatSalesQuantity(totalQuantity)}</Tag>
          <Tag color="green">{sales.length} {t('storeItemSale.list.sales.recordCount')}</Tag>
          <EyeOutlined className="view-icon" />
        </div>
      </Popover>
    );
  };

  const columns: ColumnsType<StoreItemSale> = [
    {
      title: t('storeItemSale.list.columns.storeCode'),
      dataIndex: 'storeCode',
      key: 'storeCode',
      // sorter: true,
      // sortOrder: sortField === 'storeCode' ? sortOrder : null,
      width: 120,
    },
    {
      title: t('storeItemSale.list.columns.ownerCode'),
      dataIndex: 'ownerCode',
      key: 'ownerCode',
      // sorter: true,
      // sortOrder: sortField === 'ownerCode' ? sortOrder : null,
      width: 120,
    },
    {
      title: t('storeItemSale.list.columns.itemCode'),
      dataIndex: 'itemCode',
      key: 'itemCode',
      // sorter: true,
      // sortOrder: sortField === 'itemCode' ? sortOrder : null,
      width: 120,
    },
     {
      title: t('storeItemSale.list.columns.itemName'),
      dataIndex: 'itemName',
      key: 'itemName',
      // sorter: true,
      // sortOrder: sortField === 'itemCode' ? sortOrder : null,
      width: 150,
    },
    {
      title: t('common.division'),
      dataIndex: 'divisionName',
      key: 'divisionName',
      // sorter: true,
      // sortOrder: sortField === 'itemCode' ? sortOrder : null,
      width: 150,
    },
    {
      title: t('storeItemSale.list.columns.sales'),
      dataIndex: 'sales',
      key: 'sales',
      render: (sales: Sale[] | number[]) => renderSalesPreview(sales),
      width: 200,
    },
    {
      title: t('storeItemSale.list.columns.createTime'),
      dataIndex: 'gmtCreate',
      key: 'gmtCreate',
      render: (timestamp: number) => formatDateTime(timestamp),
      // sorter: true,
      // sortOrder: sortField === 'gmtCreate' ? sortOrder : null,
      width: 180,
    },
    {
      title: t('storeItemSale.list.columns.modifyTime'),
      dataIndex: 'gmtModified',
      key: 'gmtModified',
      render: (timestamp: number) => formatDateTime(timestamp),
      // sorter: true,
      // sortOrder: sortField === 'gmtModified' ? sortOrder : null,
      width: 180,
    },
  ];

  // Filter panel fields
  const renderFilterPanel = () => (
    <FilterPanel
      onFilter={handleSearch}
      onReset={handleReset}
      title={t("menu.storeItemSale")}
      className="filter-card"
      loading={loading}
    >
      <Col span={6}>
        <Form.Item 
          name="storeCode" 
          label={t('storeItemSale.list.filter.store')}
        >
          <Select
            placeholder={t('storeItemSale.list.filter.storePlaceholder')}
            allowClear
            showSearch
            loading={storeOptionsLoading}
            onSearch={fetchStoreOptions}
            filterOption={false}
            style={{ width: '100%' }}
          >
            {storeOptions.map(store => (
              <Option key={store.code} value={store.code}>
                {store.code} - ({store.name})
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
      <Col span={6}>
        <Form.Item 
          name="divisionCode" 
          label={t('category.formLabels.divisionCode')}
        >
          <Select
            placeholder={t('category.searchPlaceholders.divisionCode')}
            allowClear
            showSearch
            loading={divisionOptionsLoading}
            onSearch={fetchDivisionOptions}
            filterOption={false}
            style={{ width: '100%' }}
          >
            {divisionOptions.map(division => (
              <Option key={division.value} value={division.value}>
                {division.label}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
      <Col span={6}>
        <Form.Item 
          name="itemCode" 
          label={t('storeItemSale.list.filter.item')}
        >
          <Select
            placeholder={t('storeItemSale.list.filter.itemPlaceholder')}
            allowClear
            showSearch
            loading={itemOptionsLoading}
            onSearch={fetchItemOptions}
            filterOption={false}
            style={{ width: '100%' }}
          >
            {itemOptions.map(item => (
              <Option key={item.code} value={item.code}>
                {item.code} - {item.name}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
      <Col span={6}>
        <Form.Item 
          name="dateRange" 
          label={t('storeItemSale.list.filter.createTime')}
        >
          <RangePicker
            style={{ width: '100%' }}
            placeholder={[t('storeItemSale.list.filter.startTime'), t('storeItemSale.list.filter.endTime')]}
          />
        </Form.Item>
      </Col>
    </FilterPanel>
  );

  return (
    <div>
        {renderFilterPanel()}
      <Card>
        <Table
          columns={columns}
          dataSource={storeItemSales}
          loading={loading}
          rowKey="id"
          pagination={{
            current,
            pageSize,
            total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              t('storeItemSale.list.pagination.displayRange', { start: range[0], end: range[1], total }),
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
          onChange={handleTableChange}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={t('common.noData')}
              />
            ),
          }}
          scroll={{ x: 1200, y: 500 }}
        />
      </Card>
    </div>
  );
};

export default StoreItemSaleList;